// Main App component — ModuERP v2.0

import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/app/store';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { designerTheme } from '@/designer/theme/theme';

// Layout components
import { MainLayout } from '@/app/layout/MainLayout';

// Auth
import { TokenManager } from '@/utils/api';

// Pages
import PrintWindowPage from '@/app/pages/PrintWindowPage';
import { PrintTabOpener } from '@/app/workspace/PrintTabOpener';
import { ProtectedDesignerRoute } from '@/app/components/ProtectedDesignerRoute';
import { EntityListPage } from '@/app/pages/EntityListPage';
import { LegacyEntityRedirect } from '@/app/pages/LegacyEntityRedirect';
import { ModulesPage } from '@/app/pages/ModulesPage';
import { LicenseManagementPage } from '@/app/pages/LicenseManagementPage';
import HelpPage from '@/app/pages/help/HelpPage';
import { WorkspaceRoute } from '@/app/workspace/WorkspaceRoute';
import { DialogProvider } from '@/shared/dialogs';

// Module system
import { getEnabledModules } from '@/app/modules';
import { initializeModules } from '@/app/modules/moduleLoader';
import { useConnection } from '@/app/contexts/ConnectionContext';
// Core modules + Designer family — lazy loaded (keeps Monaco out of the entry chunk)
import {
  ConnectionsModule,
  MarketplaceModule,
  DesignerHome,
  ConfigurationTreePage,
  SchemaDesignerPage,
  EditorPlayground,
  PrintTemplateEditorPage,
  DashboardPage,
  ThemeStudioPage,
  FormInheritanceLabPage,
  RouteFallback,
} from '@/app/core/lazyModules';

const LoginPage = React.lazy(() => import('@/pages/auth/LoginPage'));

// ─────────────────────────────────────────────
// Route guards
// ─────────────────────────────────────────────

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const hasAccessToken = Boolean(TokenManager.getAccessToken());
  if (!isAuthenticated && !hasAccessToken) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const hasAccessToken = Boolean(TokenManager.getAccessToken());
  if (isAuthenticated || hasAccessToken) {
    return <Navigate to="/connections" replace />;
  }
  return <>{children}</>;
};

// ─────────────────────────────────────────────
// App
// ─────────────────────────────────────────────

const App: React.FC = () => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const { selectedConnection } = useConnection();
  // Trigger re-render after modules are loaded
  const [, setModulesVersion] = useState(0);

  // Load all installed modules once after login (registers them in the in-memory registry)
  useEffect(() => {
    if (!isAuthenticated && !TokenManager.getAccessToken()) return;
    initializeModules().then(() => setModulesVersion(v => v + 1));
  }, [isAuthenticated]);

  // Re-check modules when the selected connection's enabled_modules change
  const enabledModulesKey = selectedConnection?.enabled_modules?.join(',') ?? '';
  useEffect(() => {
    if (!selectedConnection) return;
    initializeModules(selectedConnection.enabled_modules).then(() =>
      setModulesVersion((v) => v + 1),
    );
  }, [selectedConnection, enabledModulesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only show modules that are enabled for the current connection
  const installedModules = getEnabledModules(selectedConnection?.enabled_modules);

  return (
    <DialogProvider>
    <>
    <PrintTabOpener />
    <Routes>
      {/* Public */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Suspense fallback={<RouteFallback />}>
              <LoginPage />
            </Suspense>
          </PublicRoute>
        }
      />

      {/* Protected */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Routes>
              <Route path="/connections" element={<Suspense fallback={<RouteFallback />}><ConnectionsModule /></Suspense>} />
              <Route path="/connections/select" element={<Navigate to="/connections" replace />} />
              <Route path="/settings/connections" element={<Navigate to="/connections" replace />} />
              {/* Print window — standalone route, no MainLayout */}
              <Route path="/print" element={<PrintWindowPage />} />
              <Route path="/editor-test" element={<Suspense fallback={<RouteFallback />}><EditorPlayground /></Suspense>} />
              <Route path="/form-lab" element={<Suspense fallback={<RouteFallback />}><FormInheritanceLabPage /></Suspense>} />
              {/* Designer routes — always dark theme */}
              <Route
                path="/designer"
                element={
                  <ProtectedDesignerRoute>
                    <Suspense fallback={<RouteFallback />}>
                      <DesignerHome />
                    </Suspense>
                  </ProtectedDesignerRoute>
                }
              />
              <Route
                path="/designer/tree"
                element={
                  <ProtectedDesignerRoute>
                    <Suspense fallback={<RouteFallback />}>
                      <ConfigurationTreePage />
                    </Suspense>
                  </ProtectedDesignerRoute>
                }
              />
              <Route path="/print-templates/:layoutId/edit" element={
                <MuiThemeProvider theme={designerTheme}><CssBaseline />
                  <Suspense fallback={<RouteFallback />}><PrintTemplateEditorPage /></Suspense>
                </MuiThemeProvider>
              } />
              <Route
                path="/designer/schema/:schemaId?"
                element={
                  <ProtectedDesignerRoute>
                    <MuiThemeProvider theme={designerTheme}><CssBaseline />
                      <Suspense fallback={<RouteFallback />}><SchemaDesignerPage /></Suspense>
                    </MuiThemeProvider>
                  </ProtectedDesignerRoute>
                }
              />

              <Route
                path="/*"
                element={
                  <MainLayout>
                    <Routes>
                      <Route path="/" element={<Navigate to="/connections" replace />} />

                      {/* Core — always available */}
                      <Route path="/dashboard" element={<Suspense fallback={<RouteFallback />}><DashboardPage /></Suspense>} />
                      <Route path="/modules" element={<ModulesPage />} />
                      <Route path="/marketplace/*" element={<Suspense fallback={<RouteFallback />}><MarketplaceModule /></Suspense>} />
                      <Route path="/settings/license" element={<LicenseManagementPage />} />
                      <Route path="/settings/themes" element={<Suspense fallback={<RouteFallback />}><ThemeStudioPage /></Suspense>} />
                      <Route path="/help" element={<HelpPage />} />
                      <Route path="/help/:slug" element={<HelpPage />} />

                      {/* Dynamic routes for installed modules (driven by Marketplace) */}
                      {installedModules.map(module => (
                        <Route
                          key={module.id}
                          path={`${module.path}/*`}
                          element={
                            <Suspense fallback={<RouteFallback />}>
                              <module.component />
                            </Suspense>
                          }
                        />
                      ))}

                      {/* Configuration-driven entity pages (App mode — legacy) */}
                      <Route path="/catalogs" element={<EntityListPage />} />
                      <Route path="/catalogs/:tableName" element={<LegacyEntityRedirect />} />
                      <Route path="/documents" element={<EntityListPage />} />
                      <Route path="/documents/:tableName" element={<LegacyEntityRedirect />} />
                      <Route path="/reports/:reportId" element={<WorkspaceRoute kind="report" />} />

                      {/* Runtime App mode — workspace tabs (content in WorkspaceHost) */}
                      {/* Print form as a workspace tab; bare /print stays the standalone page */}
                      <Route path="/print/:sessionId" element={<WorkspaceRoute kind="print" />} />
                      <Route path="/administration/mail" element={<WorkspaceRoute kind="mail" />} />
                      <Route path="/app/:objectName" element={<WorkspaceRoute kind="list" />} />
                      <Route path="/app/:objectName/:recordId" element={<WorkspaceRoute kind="record" />} />

                      <Route path="*" element={<Navigate to="/connections" replace />} />
                    </Routes>
                  </MainLayout>
                }
              />
            </Routes>
          </ProtectedRoute>
        }
      />
    </Routes>
    </>
    </DialogProvider>
  );
};

export default App;
